import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { UserLiteData } from '../../../api/ApiTypes';
import { AdminActivityData, AdminActivityType } from '../../../api/endpoints/admin';
import { Input, remInPx } from '../../../ui';
import { LoadingGuard } from '../../../ui/LoadingGuard';
import { Notice } from '../../../ui/Notice';
import { PageButtonStrip } from '../../../ui/PageButtonStrip';
import { Tip } from '../../../ui/Tip';
import { useLocationScope } from '../../AppLocation';
import { useResolve } from '../../Container';
import { DataTable } from '../../common/DataTable';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { UserInfoButton } from '../shared/UserInfoButton';
import { ManageFrame } from './ManageFrame';
import { DoubleTextCell } from './common/TableElements';

const columnHelper = createColumnHelper<AdminActivityData & { info: string[] }>();

const columns = [
    columnHelper.display({
        id: 'select',
        size: remInPx(1),
        header: ({ }) => (
            <span></span>
        ),
        cell: ({ }) => (
            <span></span>
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row, {
        id: 'type',
        size: remInPx(8),
        header: () => <span>Event</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return <DoubleTextCell value1={value.type} value2={null} />;
        },
        enableSorting: false,
    }),
    columnHelper.accessor(row => row.actor, {
        id: 'actor',
        size: remInPx(16),
        header: () => <span>User</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return <Flexer height="100%" flex="row" align="center">
                <UserInfoButton value={value} />
            </Flexer>;
        },
        enableSorting: false,
    }),
    columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        size: remInPx(12),
        header: () => <span>When</span>,
        cell: info => {
            return <Tip rest={50} text={info.getValue()?.toLocaleString()}>
                <Copy size="0.9375" flush>{formatDistanceToNow(info.getValue())} ago</Copy>
            </Tip>;
        }
    }),
    columnHelper.accessor(row => row.info, {
        id: 'info',
        header: () => <span>Info</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return <DoubleTextCell value1={value[0]} value2={value[1]} />;
        },
        enableSorting: false,
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

const useActivityLogsQuery = (search: string, sort: string, startAt: number) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['admin.activity.list', search, sort, startAt],
        queryFn: async () => api.exec(v2.admin.activity.list(search, sort, startAt, 50)),
    });
};

export const ManageActivityLogsView = () => {

    useLocationScope('activity-logs', 'overview');

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const [startAt, setStartAt] = useState(0);
    const { data, isLoading } = useActivityLogsQuery(search, sort, startAt);

    // Reset startAt on search
    useEffect(() => {
        setStartAt(0);
    }, [search]);

    const items = data?.items ? data.items.map(x => ({
        ...x,
        info: dataFormatter(x.type, x.actor, x.data),
    })) : [];

    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    setSort(`${sorting[0].id}:${sorting[0].desc ? 'desc' : 'asc'}`);
                }
            }
        },
    });

    return <ManageFrame title="Activity Logs">
        <Flexer flex="row" gap="1" align="center" justify="space-between" padding="1 0">
            <Flexer flex="row" gap="1" align="center">
                <Flexer width="16">
                    <Input placeholder="Search" value={search} onChange={setSearch} />
                </Flexer>
                <Notice icon="exclamation">
                    Search only supports user, board or team IDs.
                </Notice>
            </Flexer>
            <Flexer shrink>
                <PageButtonStrip data={data} onPageChange={setStartAt} />
            </Flexer>
        </Flexer>
        <LoadingGuard loading={isLoading}>
            {data && <DataTable table={table} selectFromFirstCellOnly />}
        </LoadingGuard>
    </ManageFrame>;
};

const dataFormatter = (type: AdminActivityType, actor: UserLiteData, data: Record<string, any>) => {
    switch (type) {
        case 'account.join':
            return [
                `${actor.name} joined account ${data.accountName}`,
                '',
            ];
        case 'admin.invite.create':
            return [
                `${actor.name} created an invite for ${data.target}`,
                `InviteId: ${data.inviteId}, Type: ${data.type}`,
            ];
        case 'admin.invite.delete':
            return [
                `${actor.name} deleted an invite`,
                `InviteId: ${data.inviteId}`,
            ];
        case 'admin.sso.reset':
            return [
                `${actor.name} reset SSO config`,
                '',
            ];
        case 'admin.sso.update':
            return [
                `${actor.name} updated SSO config`,
                '',
            ];
        case 'admin.accessControl.update':
            return [
                `${actor.name} updated access control`,
                `Config: ${data.config}`,
            ];
        case 'admin.accountSettings.update':
            return [
                `${actor.name} updated account settings`,
                `Changes: ${JSON.stringify(data)}`,
            ];
        case 'admin.invite.sendBatch':
            return [
                `${actor.name} sent invites of type ${data.type}`,
                `Emails: ${data.emails}`,
            ];
        case 'admin.account.update':
            return [
                `${actor.name} updated account general info`,
                `Changes: ${JSON.stringify(data.changes)}`,
            ];
        case 'board.create':
            return [
                `${actor.name} created board ${data.boardId}`,
                `WorkspaceId: ${data.workspaceId}, FolderId: ${data.folderId}`,
            ];
        case 'board.delete':
            return [
                `${actor.name} deleted board ${data.boardId}`,
                '',
            ];
        case 'board.archive':
            return [
                `${actor.name} archived board ${data.boardId}`,
                '',
            ];
        case 'board.move':
            return [
                `${actor.name} moved board ${data.boardId}`,
                `WorkspaceId: ${data.workspaceId}, FolderId: ${data.folderId}`,
            ];
        case 'board.patch':
            return [
                `${actor.name} updated board ${data.boardId}`,
                `Changes: ${JSON.stringify(data.changes)}`,
            ];
        case 'board.join':
            return [
                `${actor.name} joined board ${data.boardId}`,
                '',
            ];
        case 'board.addMember':
            return [
                `${actor.name} added ${data.userId} to board ${data.boardId}`,
                '',
            ];
        case 'board.removeMember':
            return [
                `${actor.name} removed ${data.userId} from board ${data.boardId}`,
                '',
            ];
        case 'board.setSharing':
            return [
                `${actor.name} set sharing for board ${data.boardId}`,
                `Viewers: ${data.viewers}, Joiners: ${data.joiners}`,
            ];
        case 'board.connect':
            return [
                `${actor.name} connected to board ${data.boardId}`,
                '',
            ];
        case 'board.disconnect':
            return [
                `${actor.name} disconnected from board ${data.boardId}`,
                '',
            ];
        case 'workspace.create':
            return [
                `${actor.name} created workspace ${data.workspaceId}`,
                '',
            ];
        case 'workspace.delete':
            return [
                `${actor.name} deleted workspace ${data.workspaceId}`,
                '',
            ];
        case 'workspace.patch':
            return [
                `${actor.name} updated workspace ${data.workspaceId}`,
                `Changes: ${JSON.stringify(data.changes)}`,
            ];
        case 'workspace.join':
            return [
                `${actor.name} joined workspace ${data.workspaceId}`,
                '',
            ];
        case 'workspace.addMember':
            return [
                `${actor.name} added ${data.userId} to ${data.workspaceId}`,
                '',
            ];
        case 'workspace.leave':
            return [
                `${actor.name} left workspace ${data.workspaceId}`,
                '',
            ];
        case 'folder.create':
            return [
                `${actor.name} created folder ${data.folderId}`,
                `WorkspaceId: ${data.workspaceId}`,
            ];
        case 'folder.delete':
            return [
                `${actor.name} deleted folder ${data.folderId}`,
                '',
            ];
        case 'folder.patch':
            return [
                `${actor.name} updated folder ${data.folderId}`,
                `Changes: ${JSON.stringify(data.changes)}`,
            ];
        case 'account.removeUser':
            return [
                `${actor.name} removed user ${data.userId}`,
                '',
            ];
        case 'account.setUserRole':
            return [
                `${actor.name} set role for user ${data.userId}`,
                `RoleName: ${data.roleName}`,
            ];
        case 'invite.send':
            return [
                `${actor.name} sent invites for ${data.workspaceId}`,
                `Emails: ${data.emails}`,
            ];
        case 'invite.approve':
            return [
                `${actor.name} approved invites`,
                `Invite Ids: ${data.inviteIds}`,
            ];
        case 'invite.reject':
            return [
                `${actor.name} rejected invites`,
                `Invite Ids: ${data.inviteIds}`,
            ];
    }
};
